import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileSpreadsheet, Printer, RefreshCw, Loader2 } from 'lucide-react';
import ReceptionLayout from '../components/ReceptionLayout';
import LeadershipPageShell from '../components/LeadershipPageShell';
import LeadershipPageHeader from '../components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import VisitReportsPanel from './VisitReportsPanel';
import { Button } from '../components/ui/button';
import { formatDateTimeIST, todayIST } from '../utils/date';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { isLeadershipAdmin } from '@/utils/roleAccess';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function ReceptionReportsPage() {
  const [filter, setFilter] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const inManagementShell = pathname.startsWith('/management/');
  const brand = tenant?.branding?.primary_color || '#0f766e';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_filter: filter };
      if (filter === 'custom' && from && to) { params.date_from = from; params.date_to = to; }
      if (doctorFilter) params.doctor_id = doctorFilter;
      if (statusFilter) params.status = statusFilter;
      const { data: res } = await ax.get('/api/clinic-ops/reception/visits-report', { params });
      setData(res);
    } catch (e) {
      console.error('Report load failed', e);
      setData(null);
    }
    setLoading(false);
  }, [filter, from, to, doctorFilter, statusFilter]);

  useEffect(() => {
    if (filter === 'custom' && !(from && to)) return;
    load();
  }, [filter, from, to, doctorFilter, statusFilter, load]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const doctors = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.doctor_id && r.doctor_name) m.set(r.doctor_id, r.doctor_name); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [rows]);

  const downloadCSV = () => {
    if (rows.length === 0) return;
    const head = [
      'Date/Time', 'MRN', 'Patient', 'Phone', 'Age/Gender', 'Doctor', 'Session',
      'Visit', 'Status', 'Payment', 'Consultation ₹', 'Pharmacy ₹', 'GST ₹', 'Total ₹',
    ];
    const lines = [head.join(',')];
    rows.forEach((r) => {
      lines.push([
        r.date ? formatDateTimeIST(r.date) : '', r.mrn,
        `"${(r.patient_name || '').replace(/"/g, '""')}"`, r.patient_phone || '',
        `${r.patient_age || ''} ${r.patient_gender || ''}`.trim(),
        `"${(r.doctor_name || '').replace(/"/g, '""')}"`,
        `"${(r.session_name || '').replace(/"/g, '""')}"`,
        r.visit_type || '', r.status || '', r.payment_status || '',
        r.consultation_fee || 0, r.pharmacy_total || 0, r.pharmacy_gst || 0, r.grand_total || 0,
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visit-report-${filter}-${todayIST()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const btnCls = (!inManagementShell && isLeadershipAdmin(user))
    ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
    : undefined;
  const actions = (
    <>
      <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="report-refresh" className={btnCls}>
        {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
        Refresh
      </Button>
      <Button variant="outline" size="sm" onClick={downloadCSV} disabled={rows.length === 0} data-testid="report-export-csv" className={btnCls}>
        <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export CSV
      </Button>
      <Button variant="outline" size="sm" onClick={() => window.print()} disabled={rows.length === 0} data-testid="report-print" className={btnCls}>
        <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
      </Button>
    </>
  );

  const body = (
    <VisitReportsPanel
      filter={filter} setFilter={setFilter} from={from} setFrom={setFrom} to={to} setTo={setTo}
      doctorFilter={doctorFilter} setDoctorFilter={setDoctorFilter}
      statusFilter={statusFilter} setStatusFilter={setStatusFilter}
      doctors={doctors} rows={rows} totals={totals} data={data} loading={loading} brand={brand}
    />
  );

  if (inManagementShell) {
    return (
      <div className={LEADERSHIP_CONTENT}>
        <LeadershipPageHeader
          title="Visit reports"
          subtitle="Date-windowed visit, payment and pharmacy summary"
          actions={<div className="no-print flex flex-wrap gap-2">{actions}</div>}
        />
        {body}
      </div>
    );
  }

  if (isLeadershipAdmin(user)) {
    return (
      <LeadershipPageShell title="Visit Reports" subtitle="Date-windowed visit, payment & pharmacy summary" actions={actions}>
        {body}
      </LeadershipPageShell>
    );
  }

  return (
    <ReceptionLayout pageTitle="Visit Reports" pageSubtitle="Date-windowed visit, payment & pharmacy summary" rightActions={actions}>
      {body}
    </ReceptionLayout>
  );
}
